import { Accesscode, AccesscodeFilter } from '@leid/api-accesscodes'
import { Article } from '@leid/api-articles'
import {
  Asset,
  AssetFilter,
  AssetLockPinCode,
  AssetPhoto,
  Device,
  DeviceFilter,
  GetLockPinRequest,
  GetPinCodeForDeviceRequest,
  PinCodeForDeviceResponse,
  SetOrderAssetRequest,
  SetOrderAssetResponse,
} from '@leid/api-assets'
import type {
  CreateOrganizationRequest,
  Customer,
  CustomerOrganization,
  CustomerOrganizationBase,
  Organization,
  OrganizationsFilter,
  UpdateOrganizationRequest,
} from '@leid/api-customers'
import {
  DistanceCoordinate,
  GetSublocationsWithDevicesRequest,
  Location,
  LocationsFilter,
  ProductOpeningHoursResponse,
  Region,
  RegionsFilter,
  Sublocation,
  SublocationsFilter,
  SublocationWithDevices,
  TriggerOperationRequest,
} from '@leid/api-locations'
import { JoinRequest, JoinRequestBase, Manager } from '@leid/api-managers'
import {
  BootstrapOfferOrderProlongation,
  CreateRerentOrderItemRequest,
  CreateRerentOrderRequest,
  GetCalendarListRequest,
  GetPriceResponse,
  OfferOrder,
  OfferOrderItem,
  Order,
  OrderItem,
  OrdersFilter,
  ProductAvailabilityInfo,
  RequestReceivedEmail,
  RerentOrder,
  RerentOrderItem,
  SetPickupStatus,
} from '@leid/api-orders'
import { Payment as PaymentModule } from '@leid/api-payments'
import {
  ImageSequence,
  ImageSequencesFilter,
  ImageSequenceStep,
  ImageSequenceStepsFilter,
  Product,
  ProductCategoriesFilter,
  ProductCategory,
  ProductsSearchEvent,
} from '@leid/api-products'
import { ClientBase, PagedResponse, Response, SortingData } from '@leid/client'
import { AxiosError } from 'axios'
import Router from 'next/router'

import { ILogger } from '@leid/logger'
import { AssetPhotoUploadRequest } from 'appTypes/api-requests/assets'
import { BRegOrganizationResponse } from 'appTypes/api-requests/customer'
import { ContactFormValues } from 'appTypes/contact'
import { ArticlesPageData } from 'appTypes/content'
import { UICountry } from 'appTypes/countries'
import { GetPriceOptions, GetProlongationPriceOptions } from 'appTypes/items'
import {
  AddOrderItemToOrder,
  CreatePaymentIntRequest,
  CreateProlongation,
  DiscountCode,
  OrderInfo,
  OrderItemInfo,
  ProlongationInfo,
  UpdateOrder,
  UpdateOrderItemInOrder,
} from 'appTypes/orders'
import { SortType } from 'appTypes/shared'
import { BackendLocation } from 'appTypes/shop'
import { CoordinatesCookieData, LangCookieData } from 'utils/cookies/models'

import { IApiClient } from './interface/apiClient.interface'
import { CustomerExistsResponse } from './models/customers'
import { MissingProductsForm } from './models/missingProducts'
import { GetOfferOrderRequest, OfferRequestInfo, UpdateOfferOrderItemIntRequest } from './models/offerOrders'
import { CreateOrderIntRequest } from './models/orders'
import { PlaceSearchResult } from './models/placeSearchResult'
import { AmplifyMapConfig } from 'appTypes/amplify'

export class ApiClient extends ClientBase implements IApiClient {
  constructor(baseUrl: string, env: string, logger?: ILogger | undefined) {
    super(baseUrl, env, logger)
    this.httpClient.interceptors.response.use(
      (response) => {
        return response
      },
      (error: AxiosError) => {
        const status = error.response?.status

        if (status && [401, 403].includes(status)) {
          Router.push('/login?action=logout')
        }
        return Promise.reject(error)
      },
    )
  }

  // ====== ORDERS ======
  async getOrder(id: string | number, orderToken?: string | undefined): Promise<Response<OrderInfo | null>> {
    try {
      const tokenParam = orderToken ? `?orderToken=${orderToken}` : ''
      const result = await this.httpClient.get<Response<OrderInfo | null>>(`/api/orders/${id}${tokenParam}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getOrder error on a call', id)
    }
  }

  async getOrderClean(id: string | number): Promise<Response<OrderInfo | null>> {
    try {
      const result = await this.httpClient.get<Response<OrderInfo | null>>(`/api/orders/clean/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getOrderClean error on a call', id)
    }
  }

  async getOrders(
    checkPaymentStatus?: boolean,
    removeCanceled?: boolean,
    rentedToGTE?: Date,
    customerAndOrganizationIntersect?: boolean,
  ): Promise<Response<OrderInfo[]>> {
    try {
      const result = await this.httpClient.get<Response<OrderInfo[]>>(`/api/orders`, {
        params: {
          checkPaymentStatus: checkPaymentStatus,
          removeCanceled: removeCanceled,
          rentedToGTE: rentedToGTE,
          customerAndOrganizationIntersect: customerAndOrganizationIntersect,
        },
      })
      return result.data
    } catch (err) {
      return this.processError(err, 'getOrders error on a call')
    }
  }

  async getOrdersClean(filter: OrdersFilter): Promise<PagedResponse<Order>> {
    try {
      const result = await this.httpClient.get<PagedResponse<Order>>(`/api/orders/clean`, { params: filter })
      return result.data
    } catch (err) {
      return this.processError(err, 'getOrdersClean error on a call', filter)
    }
  }

  async createOrder(request?: CreateOrderIntRequest): Promise<Response<Order>> {
    try {
      const fulRequest: CreateOrderIntRequest = request ?? {}
      const result = await this.httpClient.post<Response<Order>>(`/api/orders`, fulRequest)
      return result.data
    } catch (err) {
      return this.processError(err, 'createOrder error on a call')
    }
  }

  async connectOrderToAccount(orderId: number, orderToken: string): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.patch<Response<boolean>>(`/api/orders/${orderId}/assign/${orderToken}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'connectOrderToAccount error on a call')
    }
  }

  async updateOrder(id: number, request: UpdateOrder): Promise<Response<OrderInfo | null>> {
    try {
      const result = await this.httpClient.put<Response<OrderInfo | null>>(`/api/orders/${id}`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'updateOrder error on a call')
    }
  }

  async cancelOrder(id: number): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.post<Response<boolean | null>>(`/api/orders/${id}/cancel`)
      return result.data
    } catch (err) {
      return this.processError(err, 'cancelOrder error on a call')
    }
  }

  // ====== RERENT ORDERS ======
  async createRerentOrder(request: CreateRerentOrderRequest): Promise<Response<RerentOrder>> {
    try {
      const result = await this.httpClient.post<Response<RerentOrder>>(`/api/rerent-orders`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'createRerentOrder error on a call')
    }
  }

  // ====== RERENT ORDER ITEMS ======
  async createRerentOrderItem(request: CreateRerentOrderItemRequest): Promise<Response<RerentOrderItem>> {
    try {
      const result = await this.httpClient.post<Response<RerentOrderItem>>(`/api/rerent-order-items`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'createRerentOrderItem error on a call')
    }
  }

  // ====== ORDER ITEMS ======
  async getOrderItem(id: string | number): Promise<Response<OrderItemInfo | null>> {
    try {
      const result = await this.httpClient.get<Response<OrderItemInfo | null>>(`/api/order_items/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getOrderItem error on a call', id)
    }
  }

  async getOrderItemWithoutCurrentCustomer(
    id: string | number,
    orderCode: string,
  ): Promise<Response<OrderItemInfo | null>> {
    try {
      const result = await this.httpClient.get<Response<OrderItemInfo | null>>(
        `/api/order_items/${id}/without_cur_customer/${orderCode}`,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'getOrderItemWithoutCurrentCustomer error on a call', id)
    }
  }

  async deleteOrderItem(id: number): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.delete<Response<boolean | null>>(`/api/order_items/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'deleteOrderItem error on a call', id)
    }
  }

  async createOrderItem(request: AddOrderItemToOrder): Promise<Response<OrderItemInfo>> {
    try {
      const result = await this.httpClient.post<Response<OrderItemInfo>>('/api/order_items', request)
      return result.data
    } catch (err) {
      return this.processError(err, 'createOrderItem error on a call', request)
    }
  }

  async updateOrderItem(id: number, request: UpdateOrderItemInOrder): Promise<Response<OrderItemInfo | null>> {
    try {
      const result = await this.httpClient.put<Response<OrderItemInfo | null>>(`/api/order_items/${id}`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'updateOrderItem error on a call', id, request)
    }
  }

  async updateOrderItemLight(id: number, request: UpdateOrderItemInOrder): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.put<Response<boolean | null>>(`/api/order_items/${id}/light`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'updateOrderItemLight error on a call', id, request)
    }
  }

  async decreaseOrderItemAmount(id: number): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.post<Response<boolean | null>>(`/api/order_items/${id}/decrease_amount`)
      return result.data
    } catch (err) {
      return this.processError(err, 'decreaseOrderItemAmount error on a call', id)
    }
  }
  async increaseOrderItemAmount(id: number): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.post<Response<boolean | null>>(`/api/order_items/${id}/increase_amount`)
      return result.data
    } catch (err) {
      return this.processError(err, 'increaseOrderItemAmount error on a call', id)
    }
  }

  async setOrderItemPickupStatus(id: number, request: SetPickupStatus): Promise<Response<OrderItem | null>> {
    try {
      const result = await this.httpClient.post<Response<OrderItem | null>>(
        `/api/order_items/${id}/pickup_statuses`,
        request,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'setOrderItemPickupStatus error on a call', id)
    }
  }

  async cancelOrderItem(id: number): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.put<Response<boolean | null>>(`/api/order_items/${id}/cancel`)
      return result.data
    } catch (err) {
      return this.processError(err, 'cancelOrderItem error on a call')
    }
  }

  // ====== PROLONGATIONS ======
  async createProlongation(
    orderItemId: number,
    request: CreateProlongation,
  ): Promise<Response<ProlongationInfo | null>> {
    try {
      const result = await this.httpClient.post<Response<ProlongationInfo | null>>(
        `/api/order_items/${orderItemId}/prolongations`,
        request,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'createProlongation error on a call', orderItemId, request)
    }
  }

  async getProlongationPrice(
    orderItemId: number,
    request: GetProlongationPriceOptions,
  ): Promise<Response<GetPriceResponse | null>> {
    try {
      const result = await this.httpClient.get<Response<GetPriceResponse | null>>(
        `/api/order_items/${orderItemId}/prolongations/price`,
        {
          params: request,
        },
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'getProlongationPrice error on a call', orderItemId, request)
    }
  }

  // ====== PRODUCTS ======
  async getProduct(id: number): Promise<Response<Product | null>> {
    try {
      const result = await this.httpClient.get<Response<Product | null>>(`/api/products-clean/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getProduct error on a call', id)
    }
  }

  async getProductPrice(id: number, request: GetPriceOptions): Promise<Response<GetPriceResponse | null>> {
    try {
      const result = await this.httpClient.get<Response<GetPriceResponse | null>>(`/api/products-clean/${id}/price`, {
        params: request,
      })
      return result.data
    } catch (err) {
      return this.processError(err, 'getProductPrice error on a call', id, request)
    }
  }

  async getProductCalendarList(
    id: number,
    request: GetCalendarListRequest,
  ): Promise<Response<ProductAvailabilityInfo>> {
    try {
      const result = await this.httpClient.get<Response<ProductAvailabilityInfo>>(
        `/api/products-clean/${id}/calendar-list`,
        {
          params: request,
        },
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'getProductCalendarList error on a call', id, request)
    }
  }

  // ====== PRODUCTS ======
  async getProductCategories(filter: ProductCategoriesFilter): Promise<PagedResponse<ProductCategory>> {
    try {
      const result = await this.httpClient.get<PagedResponse<ProductCategory>>(`/api/product-categories`, {
        params: filter,
      })
      return result.data
    } catch (err) {
      return this.processError(err, 'getProductCategories error on a call', filter)
    }
  }

  // ====== PAYMENT ======
  async createPayment(orderId: number, request: CreatePaymentIntRequest): Promise<Response<PaymentModule>> {
    try {
      const result = await this.httpClient.post<Response<PaymentModule>>(`/api/payments/orders/${orderId}`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'createPayment error on a call', orderId, request)
    }
  }

  async checkPayment(operationalId: string, transactionId: string): Promise<Response<PaymentModule | null>> {
    try {
      const params = { transactionId }
      const result = await this.httpClient.get<Response<PaymentModule | null>>(`/api/payments/${operationalId}/check`, {
        params,
      })
      return result.data
    } catch (err) {
      return this.processError(err, 'checkPayment error on a call', operationalId, transactionId)
    }
  }

  // ====== DISCOUNT CODE ======
  async getDiscountCode(discountCode: string): Promise<Response<DiscountCode | null>> {
    try {
      const result = await this.httpClient.get<Response<DiscountCode | null>>(`/api/discount-codes/${discountCode}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'validateDiscountCode error on a call', discountCode)
    }
  }

  // ====== ARTICLES ======
  async getArticles(
    query: string,
    page: number,
    items: number,
    activeSort?: SortType,
  ): Promise<Response<ArticlesPageData>> {
    try {
      const result = await this.httpClient.get<Response<ArticlesPageData>>(`/api/articles`, {
        params: {
          query: query,
          page: page,
          items: items,
          orderField: activeSort?.name,
          orderDirection: activeSort?.type,
        },
      })
      return result.data
    } catch (err) {
      return this.processError(err, 'getArticles error on a call', query, page, items, activeSort)
    }
  }

  async getArticle(id: number): Promise<Response<Article | null>> {
    try {
      const result = await this.httpClient.get<Response<Article | null>>(`/api/articles/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getArticle error on a call', id)
    }
  }

  // ====== IMAGES ======
  async uploadPhoto(file: File): Promise<Response<string>> {
    try {
      const formData = new FormData()
      formData.append('image', file)

      const result = await this.httpClient.post<Response<string>>(`/api/images`, formData)
      return result.data
    } catch (err) {
      return this.processError(err, 'uploadPhoto error on a call')
    }
  }

  // ====== CONTACT FORM ======
  async sendContactForm(request: ContactFormValues): Promise<Response<boolean>> {
    try {
      const updatedRequest = { ...request }
      if (updatedRequest.email) {
        updatedRequest.email = updatedRequest.email.toLowerCase()
      }

      const result = await this.httpClient.post<Response<boolean>>(`/api/contact-forms`, updatedRequest)
      return result.data
    } catch (err) {
      return this.processError(err, 'sendContactForm error on a call', request)
    }
  }

  async sendMissingProductsForm(request: MissingProductsForm): Promise<Response<boolean>> {
    try {
      const updatedRequest = { ...request }
      if (updatedRequest.email) {
        updatedRequest.email = updatedRequest.email.toLowerCase()
      }

      const result = await this.httpClient.post<Response<boolean>>(
        `/api/contact-forms/missing-products`,
        updatedRequest,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'sendMissingProductsForm error on a call', request)
    }
  }

  // ====== CUSTOMERS ======
  async getCustomer(id: number): Promise<Response<Customer | null>> {
    try {
      const result = await this.httpClient.get<Response<Customer | null>>(`/api/customers/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getCustomer error on a call', id)
    }
  }

  async getCustomerProfile(): Promise<Response<Customer | null>> {
    try {
      const result = await this.httpClient.get<Response<Customer | null>>(`/api/customers`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getCustomerProfile error on a call')
    }
  }

  async setNewsletter(email: string): Promise<Response<boolean>> {
    try {
      const lowerEmail = email?.toLowerCase()
      const result = await this.httpClient.post<Response<boolean>>(`/api/customers/email/${lowerEmail}/newsletters`)
      return result.data
    } catch (err) {
      return this.processError(err, 'setNewsletter error on a call')
    }
  }

  async setChangePassword(email: string, changePassword: boolean): Promise<Response<boolean>> {
    try {
      const lowerEmail = email?.toLowerCase()
      const result = await this.httpClient.post<Response<boolean>>(
        `/api/customers/email/${lowerEmail}/changePassword/${changePassword}`,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'setChangePassword error on a call')
    }
  }

  async getOrganization(id: number): Promise<Response<Organization | null>> {
    try {
      const result = await this.httpClient.get<Response<Organization | null>>(`/api/organizations/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getOrganization error on a call', id)
    }
  }

  async refreshOrganizationInfo(id: number): Promise<Response<Organization | null>> {
    try {
      const response = await this.httpClient.put<Response<Organization | null>>(`/api/organizations/${id}/refresh`)
      return response.data
    } catch (err) {
      return this.processError(err, 'refreshOrganizationInfo error on a call', id)
    }
  }

  async requestToJoinOrganization(customerOrganization: CustomerOrganizationBase): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.post<Response<boolean | null>>(
        `/api/organizations/${customerOrganization?.organizationId}/customer-organizations/request-join`,
        customerOrganization,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'requestToJoinOrganization error on a call', customerOrganization)
    }
  }

  async getCustomerOrganizationFromHash(request: GetOfferOrderRequest): Promise<Response<CustomerOrganization>> {
    try {
      const result = await this.httpClient.post<Response<CustomerOrganization>>(
        `/api/organizations/customer-organizations/decrypt`,
        request,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'getOfferRequestInfo error on a call')
    }
  }

  async getCustomerOrganization(
    organizationId: number,
    customerId: number,
  ): Promise<Response<CustomerOrganization | null>> {
    try {
      const result = await this.httpClient.get<Response<CustomerOrganization | null>>(
        `/api/organizations/${organizationId}/customer-organizations/${customerId}`,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'getCustomerOrganization error on a call', organizationId, customerId)
    }
  }

  async updateCustomerOrganization(request: CustomerOrganization): Promise<Response<CustomerOrganization | null>> {
    try {
      const result = await this.httpClient.put<Response<CustomerOrganization | null>>(
        `/api/organizations/${request.organizationId}/customer-organizations/${request.customerId}`,
        request,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'updateCustomerOrganization error on a call', request)
    }
  }

  async checkCustomerExists(email: string): Promise<Response<CustomerExistsResponse>> {
    try {
      const result = await this.httpClient.get<Response<CustomerExistsResponse>>(`/api/customers/email/${email}/exists`)
      return result.data
    } catch (err) {
      return this.processError(err, 'checkCustomerExists error on a call', email)
    }
  }

  async getCustomerOrganizations(): Promise<Response<CustomerOrganization[] | null>> {
    try {
      const result = await this.httpClient.get<Response<CustomerOrganization[] | null>>(
        `/api/organizations/customer-organizations`,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'getCustomerOrganizations error on a call')
    }
  }

  async getOrganizations(filter: OrganizationsFilter): Promise<PagedResponse<Organization>> {
    try {
      const result = await this.httpClient.get<PagedResponse<Organization>>(`/api/organizations`, { params: filter })
      return result.data
    } catch (err) {
      return this.processError(err, 'getOrganizations error on a call', filter)
    }
  }

  async getOrganizationFromBReg(organizationNumber: string): Promise<Response<BRegOrganizationResponse | null>> {
    try {
      const result = await fetch(`https://data.brreg.no/enhetsregisteret/api/enheter/${organizationNumber}`)
      const data = (await result.json()) as unknown as BRegOrganizationResponse
      return { success: true, data }
    } catch (err) {
      return this.processError(err, 'getOrganizationFromBReg error on a call', organizationNumber)
    }
  }

  async createOrganization(request: CreateOrganizationRequest): Promise<Response<Organization | null>> {
    try {
      const result = await this.httpClient.post<Response<Organization | null>>(`/api/organizations`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'createOrganization error on a call', request)
    }
  }

  async addCustomerToOrganization(customerId: number, organizationId: number): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.post<Response<boolean | null>>(
        `/api/customers/${customerId}/organizations`,
        {
          organizationId,
        },
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'addCustomerToOrganization error on a call', customerId, organizationId)
    }
  }

  async updateOrganization(id: number, request: UpdateOrganizationRequest): Promise<Response<Organization | null>> {
    try {
      const result = await this.httpClient.put<Response<Organization | null>>(`/api/organizations/${id}`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'updateOrganization error on a call', id, request)
    }
  }

  // ====== LOCATIONS ======
  async getLocations(filter: LocationsFilter): Promise<Response<BackendLocation[]>> {
    try {
      const result = await this.httpClient.get<Response<BackendLocation[]>>(`/api/location`, { params: filter })
      return result.data
    } catch (err) {
      return this.processError(err, 'getLocations error on a call')
    }
  }

  async getSubLocations(filter: SublocationsFilter): Promise<PagedResponse<Sublocation>> {
    try {
      const result = await this.httpClient.get<PagedResponse<Sublocation>>(`/api/sublocations`, { params: filter })
      return result.data
    } catch (err) {
      return this.processError(err, 'getSubLocations error on a call')
    }
  }

  async triggerSubLocationOutput(request: TriggerOperationRequest): Promise<Response<boolean | null>> {
    try {
      const response = await this.httpClient.post<Response<boolean | null>>(`/api/sublocations/output`, {
        triggerRequest: request,
      })
      return response.data
    } catch (err) {
      return this.processError(err, 'triggerSubLocationOutput error on a call')
    }
  }

  async getSublocationDevices(request: GetSublocationsWithDevicesRequest): Promise<Response<SublocationWithDevices[]>> {
    try {
      const response = await this.httpClient.get<Response<SublocationWithDevices[]>>(`/api/locations/devices`, {
        params: request,
      })
      return response.data
    } catch (err) {
      return this.processError(err, 'getSublocationDevices error on a call')
    }
  }

  async getAssetDevices(filter: DeviceFilter): Promise<PagedResponse<Device>> {
    try {
      const result = await this.httpClient.get<PagedResponse<Device>>(`/api/assets/devices`, { params: filter })
      return result.data
    } catch (err) {
      return this.processError(err, 'getAssetDevices error on a call')
    }
  }

  async getPinCodeForDevice(request: GetPinCodeForDeviceRequest): Promise<Response<PinCodeForDeviceResponse | null>> {
    try {
      const result = await this.httpClient.post<Response<PinCodeForDeviceResponse | null>>(
        `/api/assets/devices/pin-codes`,
        request,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'getPinCodeForDevice error on a call', request)
    }
  }

  async getRealLocations(
    filter: LocationsFilter,
    sortingData?: SortingData,
    distanceCoordinates?: DistanceCoordinate,
  ): Promise<PagedResponse<Location>> {
    try {
      const params = { ...filter, ...sortingData, ...distanceCoordinates }
      const result = await this.httpClient.get<PagedResponse<Location>>(`/api/locations`, { params })
      return result.data
    } catch (err) {
      return this.processError(err, 'getRealLocations error on a call')
    }
  }

  async searchLocations(search: string): Promise<Response<PlaceSearchResult[]>> {
    try {
      const result = await this.httpClient.get<Response<PlaceSearchResult[]>>(`/api/locations/search`, {
        params: { searchTerm: search },
      })
      return result.data
    } catch (err) {
      return this.processError(err, 'searchLocations error on a call')
    }
  }

  // ====== LOGS ======
  async createLog(message: string): Promise<Response<boolean>> {
    try {
      const request = { message }
      const result = await this.httpClient.post<Response<boolean>>(`/api/logs`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'createLog error on a call', message)
    }
  }

  // ====== PDFs ======
  async generateReceiptPDF(orderId: number): Promise<Response<boolean | null>> {
    try {
      const result = await this.httpClient.post<Response<boolean | null>>(`/api/orders/${orderId}/pdfs/receipt`)
      return result.data
    } catch (err) {
      return this.processError(err, 'generateReceiptPDF error on a call', orderId)
    }
  }

  // ====== REGIONS ======
  async getRegions(
    filter: RegionsFilter,
    sortingData?: SortingData,
    distanceCoordinates?: DistanceCoordinate,
  ): Promise<PagedResponse<Region>> {
    try {
      const params = { ...filter, ...sortingData, ...distanceCoordinates }

      const response = await this.httpClient.get<PagedResponse<Region>>('/api/regions', {
        params,
      })
      return response.data
    } catch (err) {
      return this.processError(err, 'getRegions error on a call', filter)
    }
  }

  async getRegion(id: number): Promise<Response<Region | null>> {
    try {
      const result = await this.httpClient.get<Response<Region | null>>(`/api/regions/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getRegion error on a call', id)
    }
  }

  // ====== MANAGERS ======
  async getManager(id: number): Promise<Response<Manager | null>> {
    try {
      const result = await this.httpClient.get<Response<Manager | null>>(`/api/managers/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getManager error on a call', id)
    }
  }

  async createManagerJoinRequest(joinRequest: JoinRequestBase): Promise<Response<JoinRequest>> {
    try {
      const result = await this.httpClient.post<Response<JoinRequest>>(`/api/managers/join-requests`, joinRequest)
      return result.data
    } catch (err) {
      return this.processError(err, 'createManagerJoinRequest error on a call', joinRequest)
    }
  }

  async getAssets(filter: AssetFilter): Promise<PagedResponse<Asset>> {
    try {
      const params = { ...filter }

      const response = await this.httpClient.get<PagedResponse<Asset>>('/api/assets', {
        params,
      })
      return response.data
    } catch (err) {
      return this.processError(err, 'getAssets error on a call', filter)
    }
  }

  async getAsset(id: number): Promise<Response<Asset | null>> {
    try {
      const response = await this.httpClient.get<Response<Asset | null>>(`/api/assets/${id}`)
      return response.data
    } catch (err) {
      return this.processError(err, 'getAsset error on a call', id)
    }
  }

  async getLockPin(request: GetLockPinRequest): Promise<Response<AssetLockPinCode | null>> {
    try {
      const result = await this.httpClient.post<Response<AssetLockPinCode | null>>(`/api/assets/lockpin`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'getLockPin error on a call', request)
    }
  }

  async uploadAssetImage(assetPhoto: AssetPhotoUploadRequest): Promise<Response<AssetPhoto>> {
    try {
      const body = new FormData()
      Object.keys(assetPhoto).forEach((key: string) => {
        body.append(key, (assetPhoto as any)[key])
      })
      if (assetPhoto.imageUrl !== null && typeof assetPhoto.imageUrl !== 'string') {
        if (assetPhoto.imageUrl instanceof Blob) {
          const imageUrl = await this.uploadImage(assetPhoto.imageUrl)
          assetPhoto.imageUrl = imageUrl.data
        }
      }

      const response = await this.httpClient.post(`/api/asset-photo`, { assetPhoto })
      return response.data
    } catch (error) {
      return this.processError(error, 'uploadAssetImage error on a call', assetPhoto)
    }
  }

  async setOrderAssetState(request: SetOrderAssetRequest): Promise<Response<SetOrderAssetResponse | null>> {
    try {
      const result = await this.httpClient.post<Response<SetOrderAssetResponse | null>>(`/api/assets/states`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'setOrderAssetState error on a call', request)
    }
  }

  private uploadImage = async (imageFile: Blob): Promise<Response<string | null>> => {
    try {
      const formData = new FormData()
      formData.append('image', imageFile)

      const tempUrlResponse = await this.httpClient.post<Response<string | null>>(`/api/image-upload-s3`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return tempUrlResponse.data
    } catch (err) {
      console.log('Error on image upload', err)
      return this.processError(err, 'uploadAssetImage error on a call', imageFile)
    }
  }

  // ====== OPENING HOURS ======
  async getProductOpeningHours(productId: number): Promise<Response<ProductOpeningHoursResponse[]>> {
    try {
      const result = await this.httpClient.get<Response<ProductOpeningHoursResponse[]>>(
        `/api/opening-hours/products/${productId}`,
      )
      return result.data
    } catch (err) {
      return this.processError(err, 'getProductOpeningHours error on a call', productId)
    }
  }

  // ====== OFFER ORDER ITEMS ======
  async getOfferRequestInfo(request: GetOfferOrderRequest): Promise<Response<OfferRequestInfo>> {
    try {
      const result = await this.httpClient.post<Response<OfferRequestInfo>>(`/api/offer-orders/decrypt`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'getOfferRequestInfo error on a call')
    }
  }

  async getOfferOrder(id: number): Promise<Response<OfferOrder | null>> {
    try {
      const result = await this.httpClient.get<Response<OfferOrder>>(`/api/offer-orders/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getOfferOrder error on a call', id)
    }
  }

  async updateOfferOrderItem(
    id: number,
    request: UpdateOfferOrderItemIntRequest,
  ): Promise<Response<OfferOrderItem | null>> {
    try {
      const result = await this.httpClient.put<Response<OfferOrderItem | null>>(`/api/offer-order-items/${id}`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'updateOfferOrderItem error on a call')
    }
  }

  async declineOfferOrderItem(id: number): Promise<Response<OfferOrderItem | null>> {
    try {
      const response = await this.httpClient.post<Response<OfferOrderItem>>(`/api/offer-order-items/${id}/decline`)
      return response.data
    } catch (err) {
      return this.processError(err, 'declineOfferOrderItem error on a call', id)
    }
  }

  async bootstrapOfferOrderProlongation(request: BootstrapOfferOrderProlongation): Promise<Response<boolean>> {
    try {
      const result = await this.httpClient.post<Response<boolean>>(`/api/bootstrap/offer-orders/prolongations`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'bootstrapOfferOrderProlongation error on a call', request)
    }
  }

  // ====== COUNTRIES ======
  async getCountryByEnviroment(): Promise<Response<UICountry | null>> {
    try {
      const result = await this.httpClient.get<Response<UICountry | null>>(`/api/countries/env`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getCountryByEnviroment error on a call')
    }
  }

  // ====== Events ======
  async sendSearchEvent(request: ProductsSearchEvent): Promise<Response<void>> {
    try {
      const result = await this.httpClient.post<Response<void>>(`/api/logs/searches`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'sendSearchEvent error on a call', request)
    }
  }

  // ====== Accesscodes ======
  async getAccesscodes(accesscodeFilter: AccesscodeFilter): Promise<PagedResponse<Accesscode>> {
    try {
      const result = await this.httpClient.get<PagedResponse<Accesscode>>(`/api/accesscodes`, {
        params: accesscodeFilter,
      })
      return result.data
    } catch (err) {
      return this.processError(err, 'getAccesscodes error on a call', accesscodeFilter)
    }
  }

  // ====== LogIn ======
  async selectOrganizationOrPersonalAccount(organizationId: number | undefined): Promise<Response<boolean>> {
    try {
      const result = await this.httpClient.put<Response<boolean>>(`/api/accounts/select`, { organizationId })
      return result.data
    } catch (err) {
      return this.processError(err, 'selectOrganizationOrPersonalAccount error on a call', organizationId)
    }
  }

  async getSelectedOrganization(): Promise<Response<Organization | null>> {
    try {
      const result = await this.httpClient.get<Response<Organization | null>>(`/api/organizations/profile`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getSelectedOrganization error on a call')
    }
  }

  // ====== Emails ======
  async sendOrderRequestReceivedEmail(request: RequestReceivedEmail): Promise<Response<boolean>> {
    try {
      const response = await this.httpClient.post<Response<boolean>>('/api/emails/order-request-received', request)
      return response.data
    } catch (err) {
      return this.processError(err, 'sendOrderRequestReceivedEmail error on a call', request)
    }
  }

  // ====== Image Sequence ======
  async getImageSequence(id: number): Promise<Response<ImageSequence | null>> {
    try {
      const result = await this.httpClient.get<Response<ImageSequence | null>>(`/api/products/image-sequences/${id}`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getImageSequence error on a call', id)
    }
  }

  async getImageSequences(filter: ImageSequencesFilter): Promise<PagedResponse<ImageSequence>> {
    try {
      const response = await this.httpClient.get<PagedResponse<ImageSequence>>('/api/products/image-sequences', {
        params: filter,
      })
      return response.data
    } catch (err) {
      return this.processError(err, 'getImageSequences error on a call', filter)
    }
  }

  async getImageSequenceSteps(filter: ImageSequenceStepsFilter): Promise<PagedResponse<ImageSequenceStep>> {
    try {
      const response = await this.httpClient.get<PagedResponse<ImageSequenceStep>>(
        '/api/products/image-sequences/steps',
        {
          params: filter,
        },
      )
      return response.data
    } catch (err) {
      return this.processError(err, 'getImageSequenceSteps error on a call', filter)
    }
  }

  async setCustomerCoordinates(request: CoordinatesCookieData | undefined): Promise<Response<boolean>> {
    try {
      const result = await this.httpClient.post<Response<boolean>>(`/api/coordinates`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'setCustomerCoordinates error on a call', request)
    }
  }

  async setLangCookie(request: LangCookieData | undefined): Promise<Response<boolean>> {
    try {
      const result = await this.httpClient.post<Response<boolean>>(`/api/languages`, request)
      return result.data
    } catch (err) {
      return this.processError(err, 'setLangCookie error on a call', request)
    }
  }

  // ====== Configs ======
  async getMapConfig(): Promise<Response<any>> {
    try {
      const result = await this.httpClient.get<Response<AmplifyMapConfig | null>>(`/api/map`)
      return result.data
    } catch (err) {
      return this.processError(err, 'getMapConfig error on a call')
    }
  }
}
